import pbfe from "./pbfe.js"
import welcomeScreen from "./welcome-screen.js";
import changelogScreen from "./changelog-screen.js";
import blogScreen from "./blog-screen.js";
import blogPostScreen from "./blog-post-screen.js";
import aboutScreen from "./about-screen.js";
import privacyScreen from "./privacy-screen.js";
import contactScreen from "./contact-screen.js";
import notFoundScreen from "./not-found-screen.js";
import DndHandler from "./dnd.js";
import loadingDialog from "./loading-dialog.js";
import editor from "./editor.js";
import toast from "./toast.js";
import i18n, { _, retranslate } from "./i18n.js";
import { touchToMouseEvent } from "./utils.js";
import { getPostBySlug } from "./blog-posts.js";
import { initConsentBanner } from "./consent-banner.js";

function registerServiceWorker() {
    if ("serviceWorker" in navigator && process.env.NODE_ENV == "production") {
        navigator.serviceWorker.register(new URL("sw.js", import.meta.url));
    }
}

export const LANG_URL_MAP = {
    "en-us": "",
    "fr-fr": "fr",
    "de-de": "de",
    "ru-ru": "ru",
    "pt-br": "pt",
    "nl-nl": "nl",
    "id-id": "id",
    "ja-jp": "ja",
    "ko-kr": "ko",
    "vi-vn": "vn",
    "zh-cn": "zh",
    "zh-tw": "zh-tw"
};

export function getLang() {
    // Priority 1: URL Path
    var path = window.location.pathname;
    // Remove trailing slash for consistency
    if (path.endsWith("/")) path = path.slice(0, -1);
    
    var segments = path.split("/");
    var firstSegment = segments[1]; // path starts with /

    for (const [lang, prefix] of Object.entries(LANG_URL_MAP)) {
        if (prefix === "") continue; // Skip default
        if (firstSegment === prefix) return lang;
    }
    
    // Priority 2: LocalStorage (only if at root or default path)
    if (!firstSegment) {
        var saved = localStorage.getItem("lang");
        if (saved && LANG_URL_MAP[saved] !== undefined) return saved;
    }
    
    return "en-us";
}

export function getCleanPath() {
    var path = window.location.pathname;
    if (path.endsWith("/")) path = path.slice(0, -1);
    
    var segments = path.split("/");
    var firstSegment = segments[1];
    
    for (const prefix of Object.values(LANG_URL_MAP)) {
        if (prefix && firstSegment === prefix) {
            var newPath = path.substring(prefix.length + 1);
            if (newPath === "") return "/";
            return newPath;
        }
    }
    
    if (path === "") return "/";
    return path;
}

function updateCanonical(lang, route) {
    var link = document.querySelector("link[rel='canonical']");
    if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
    }
    
    // Base URL
    var url = "https://avatarcropper.app/";
    var prefix = LANG_URL_MAP[lang];
    
    if (prefix) {
        url += prefix;
        // Set generic language code for html tag
        var shortLang = lang.split("-")[0];
        if (lang === "zh-cn") shortLang = "zh-Hans";
        if (lang === "zh-tw") shortLang = "zh-Hant";
        document.documentElement.lang = shortLang;
    } else {
        document.documentElement.lang = "en";
    }

    // Append route if not root
    if (route && route !== "/") {
        // Ensure route starts with /
        if (!route.startsWith("/")) route = "/" + route;
        // If url ends with /, remove it before appending route to avoid //
        if (url.endsWith("/")) url = url.slice(0, -1);
        url += route;
    }
    
    link.href = url;
}

function updateSEOTags(lang, route) {
    var titleKey, descKey;

    if (route === "/changelog") {
        titleKey = "Changelog - Avatar Cropper";
        descKey = "See what's new in Avatar Cropper. Latest updates, features, and bug fixes.";
    } else if (route === "/blog") {
        titleKey = "Blog";
        descKey = "Learn how to use Avatar Cropper to create perfect avatars for your social media profiles.";
    } else if (route && route.startsWith("/blog/")) {
        // Dynamic blog post SEO
        var slug = route.replace("/blog/", "");
        var post = getPostBySlug(slug);
        if (post) {
            titleKey = post.title;
            descKey = post.summary;
        } else {
            titleKey = "Post Not Found - Avatar Cropper";
            descKey = "The requested blog post could not be found.";
        }
    } else if (route === "/about") {
        titleKey = "About - Avatar Cropper";
        descKey = "Learn more about Avatar Cropper, a free online tool to crop avatars for social media.";
    } else if (route === "/privacy") {
        titleKey = "Privacy Policy - Avatar Cropper";
        descKey = "How Avatar Cropper handles your data, local image processing, and advertising cookies (Google AdSense).";
    } else if (route === "/contact") {
        titleKey = "Contact Us - Avatar Cropper";
        descKey = "Get in touch with the Avatar Cropper team by email for questions, feedback, or privacy requests.";
    } else {
        // Home or Editor
        titleKey = "Avatar Cropper - Quickly Crop Avatar for Discord, X, etc.";
        descKey = "Quick and accurate avatar cropper tool. Crop perfect avatar for Discord, X (Twitter), etc. No signup required.";
    }

    // Use translations if available, otherwise fallback to English key or raw string (for dynamic content)
    var title = _(titleKey) === titleKey ? titleKey : _(titleKey);
    var desc = _(descKey) === descKey ? descKey : _(descKey);

    document.title = title;
    
    // Meta Description
    var metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) metaDesc.content = desc;
    else {
        metaDesc = document.createElement("meta");
        metaDesc.name = "description";
        metaDesc.content = desc;
        document.head.appendChild(metaDesc);
    }

    // OG Tags
    var ogTitle = document.querySelector("meta[property='og:title']");
    if (ogTitle) ogTitle.content = title;
    
    var ogDesc = document.querySelector("meta[property='og:description']");
    if (ogDesc) ogDesc.content = desc;
    
    var ogUrl = document.querySelector("meta[property='og:url']");
    if (ogUrl) {
        var currentUrl = document.querySelector("link[rel='canonical']").href;
        ogUrl.content = currentUrl;
    }

    // Twitter Tags
    var twitterTitle = document.querySelector("meta[name='twitter:title']");
    if (twitterTitle) twitterTitle.content = title;
    
    var twitterDesc = document.querySelector("meta[name='twitter:description']");
    if (twitterDesc) twitterDesc.content = desc;

    // Update Canonical and Lang attribute
    updateCanonical(lang, route);
}

export function handleRouting() {
    var path = getCleanPath();
    var lang = getLang();

    // Update SEO tags based on current route and language
    updateSEOTags(lang, path);

    if (path === "/avatar-cropper-editor") {
        if (editor.hasImage()) {
            welcomeScreen.hide();
            changelogScreen.hide();
            blogScreen.hide();
            blogPostScreen.hide();
            aboutScreen.hide();
            privacyScreen.hide();
            contactScreen.hide();
            notFoundScreen.hide();
            editor.show();
        } else {
            // Redirect to home if no image
            var prefix = LANG_URL_MAP[lang];
            var rootPath = prefix ? "/" + prefix : "/";

            history.replaceState(null, "", rootPath);
            editor.hide();
            changelogScreen.hide();
            blogScreen.hide();
            blogPostScreen.hide();
            aboutScreen.hide();
            privacyScreen.hide();
            contactScreen.hide();
            notFoundScreen.hide();
            welcomeScreen.show();
        }
    } else if (path === "/changelog") {
        welcomeScreen.hide();
        editor.hide();
        aboutScreen.hide();
        blogScreen.hide();
        blogPostScreen.hide();
        privacyScreen.hide();
        contactScreen.hide();
        notFoundScreen.hide();
        changelogScreen.show();
    } else if (path === "/blog") {
        welcomeScreen.hide();
        editor.hide();
        aboutScreen.hide();
        changelogScreen.hide();
        blogPostScreen.hide();
        privacyScreen.hide();
        contactScreen.hide();
        notFoundScreen.hide();
        blogScreen.show();
    } else if (path && path.startsWith("/blog/")) {
        welcomeScreen.hide();
        editor.hide();
        aboutScreen.hide();
        changelogScreen.hide();
        blogScreen.hide();
        privacyScreen.hide();
        contactScreen.hide();
        notFoundScreen.hide();

        var slug = path.replace("/blog/", "");
        blogPostScreen.init(null, slug); // Pass slug to init
        blogPostScreen.show();
    } else if (path === "/about") {
        welcomeScreen.hide();
        editor.hide();
        changelogScreen.hide();
        blogScreen.hide();
        blogPostScreen.hide();
        privacyScreen.hide();
        contactScreen.hide();
        notFoundScreen.hide();
        aboutScreen.show();
    } else if (path === "/privacy") {
        welcomeScreen.hide();
        editor.hide();
        changelogScreen.hide();
        blogScreen.hide();
        blogPostScreen.hide();
        aboutScreen.hide();
        contactScreen.hide();
        notFoundScreen.hide();
        privacyScreen.show();
    } else if (path === "/contact") {
        welcomeScreen.hide();
        editor.hide();
        changelogScreen.hide();
        blogScreen.hide();
        blogPostScreen.hide();
        aboutScreen.hide();
        privacyScreen.hide();
        notFoundScreen.hide();
        contactScreen.show();
    } else if (path === "/") {
        editor.hide();
        changelogScreen.hide();
        blogScreen.hide();
        blogPostScreen.hide();
        aboutScreen.hide();
        privacyScreen.hide();
        contactScreen.hide();
        notFoundScreen.hide();
        welcomeScreen.show();
    } else {
        editor.hide();
        changelogScreen.hide();
        blogScreen.hide();
        blogPostScreen.hide();
        aboutScreen.hide();
        privacyScreen.hide();
        contactScreen.hide();
        welcomeScreen.hide();
        notFoundScreen.show();
    }
}

async function bootstrap() {
    registerServiceWorker();
    document.body.innerHTML = "";

    initConsentBanner();

    var lang = getLang();

    var container = new pbfe.Container;
    container.element.style.backgroundColor = "#1e1e1e";
    container.createShadow();

    toast.init(container);

    try {
        await i18n.init();
        try {
            await i18n.loadLanguage(lang);
            localStorage.setItem("lang", lang);
        }
        catch {
            toast.show("Failed to load translations for " + lang);
        }
    }
    catch {
        toast.show("Failed to initialize i18n.");
    }

    editor.init(container);
    changelogScreen.init(container);
    blogScreen.init(container);
    blogPostScreen.init(container); // Initial init (container only)
    aboutScreen.init(container);
    privacyScreen.init(container);
    contactScreen.init(container);
    notFoundScreen.init(container);
    loadingDialog.init(container);

    loadingDialog.setProgress(-1);
    loadingDialog.show();

    welcomeScreen.init(container, function() {
        handleRouting();
        loadingDialog.hide();
    });

    window.addEventListener("popstate", function() {
        handleRouting();
    });

    var dnd = new DndHandler(container, document.body);
    dnd.addListener(e => {
        editor.open(e.detail, function() {
            var lang = getLang();
            var prefix = LANG_URL_MAP[lang];
            var path = "/avatar-cropper-editor";
            if (prefix) path = "/" + prefix + path;

            history.pushState(null, "", path);
            handleRouting();
        });
    });

    if ("ontouchmove" in document) {
        document.addEventListener("touchmove", touchToMouseEvent, { passive: true });
        document.addEventListener("touchend", touchToMouseEvent, { passive: true });
    }

    window.scrollTo(0, 1);
}
// One-time app bootstrap. Called exactly once at startup. Navigation and
// language switches must NOT call this again — use navigate()/switchLanguage()
// which update the UI in place instead of tearing down the whole DOM (which
// would also discard any image the user is editing).
bootstrap();

export function navigate(path) {
    history.pushState(null, "", path);
    handleRouting();
}

export async function switchLanguage(lang) {
    localStorage.setItem("lang", lang);

    var cleanPath = getCleanPath();
    var newPrefix = LANG_URL_MAP[lang];
    var newPath = cleanPath;
    if (newPrefix) {
        if (cleanPath === "/") newPath = "/" + newPrefix;
        else newPath = "/" + newPrefix + cleanPath;
    }

    history.pushState(null, "", newPath);

    try {
        await i18n.loadLanguage(lang);
    } catch (e) {
        // keep current strings if the fetch fails
    }

    // Re-translate every registered UI element in place (no rebuild).
    retranslate();

    // Update SEO signals + show the correct screen for the new URL/language.
    handleRouting();
}

export default bootstrap;
